import {
    fetchActivityDataSuccessV1,
    fetchActivityDataFail,
    fetchActivityDataStart,
    fetchActivityDataSuccess
} from 'actions/multiSportEditActions';
import { takeEvery, call, put, all, select } from 'redux-saga/effects';
import { gcApi } from 'config/axios';
import { fetchChildActivityData } from 'sagas/multiSportEditSagas';
import MetricsUtils from 'utils/MetricsUtils';
import { duplicateLastItemOfEveryMember, toObject } from 'reducers/multiSportEdit/utils/genericUtils';

/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityDataDTO').ActivityDataDTO} ActivityDataDTO */
/** @typedef {import('reducers/multiSportEdit/types/dto/ActivityMetricsDTO').ActivityMetricsDTO} ActivityMetricsDTO */
/** @typedef {import('reducers/multiSportEdit/types/entity/LegMetrics').LegMetrics} LegMetrics */
/** @typedef {import('reducers/multiSportEdit/types/entity/MetricsByKey').MetricsByKey} MetricsByKey */
/** @typedef {import('reducers/multiSportEdit/types/entity/LegIndexData').LegIndexData} LegIndexData */

/** @typedef {{[key: string]: LegMetrics}} LegMetricsByActivityId */

const { DEFAULT_MAX_CHART_SIZE, DEFAULT_MAX_POLYLINE_SIZE } = MetricsUtils;
const maxChartSize = 200;
const maxPolylineSize = 200;

const createMetricsParameters = ({ maxChartSize, maxPolylineSize }) =>
    `maxChartSize=${maxChartSize}` +
    `&maxPolylineSize=${maxPolylineSize}` +
    '&metric=sumDistance' +
    '&metric=sumDuration' +
    '&metric=directTimestamp' +
    '&metric=directSpeed' +
    '&metric=directPower' +
    '&metric=directElevation' +
    '&metric=directHeartRate';

/**
 * @param {number[]} childIds
 */
function* fetchLegMetrics(childIds) {
    const metricsParameters = createMetricsParameters({ maxChartSize, maxPolylineSize });

    /** @type {{ data: ActivityMetricsDTO }[]} */
    const responses = yield all(
        childIds.map((id) => call(gcApi.get, `/activity-service/activity/${id}/details?${metricsParameters}`))
    );
    const legMetricsByActivityId = responses
        .map(({ data }) => {
            /** @type {LegMetrics} */
            const legMetrics = MetricsUtils.mergeMetricsAndDescriptors(data);
            return /** @type {[string, LegMetrics]}*/ ([data.activityId.toString(), legMetrics]);
        })
        .reduce(toObject, /** @type {LegMetricsByActivityId} */ ({}));

    return legMetricsByActivityId;
}

/**
 * @param {Array<LegMetrics & { typeId: number; typeKey: string }>} allMetrics
 * @returns {LegIndexData[]}
 */
const calculateLegIndexData = (allMetrics) => {
    const sum = { metricsCount: 0, polylineCount: 0 };
    /** @type {LegIndexData[]} */
    const legIndexData = [];

    // add items marking the start of all the legs
    for (const currentItem of allMetrics) {
        const legStart = {
            activityId: currentItem.activityId,
            typeKey: currentItem.typeKey,
            typeId: currentItem.typeId,
            metricsStartIdx: sum.metricsCount,
            polylineStartIdx: sum.polylineCount,
            timestamp: null
        };
        legIndexData.push(legStart);
        sum.metricsCount += currentItem.metricsCount;
        const polylineCount = currentItem.geoPolylineDTO ? currentItem.geoPolylineDTO.polyline.length : 0;
        sum.polylineCount += polylineCount;
    }

    // add last item,  marking the start of the "imaginary" leg (and what's more important, the end of preceeding leg)
    // as the last item is duplication of before the last item, "imaginary" leg can start at exactly the last item
    legIndexData.push({
        activityId: null,
        typeKey: null,
        typeId: null,
        metricsStartIdx: sum.metricsCount - 1,
        polylineStartIdx: sum.polylineCount - 1,
        timestamp: null
    });

    return legIndexData;
};

/**
 * sumDuration always starts from 0 for a leg
 * @param {Array<number[]>} sumDurations
 */
const joinSumDuration = (sumDurations) => {
    const joined = [];
    let startValue = 0;
    for (const current of sumDurations) {
        const adjustedCurrent = current.map((x) => x + startValue);
        startValue = adjustedCurrent[adjustedCurrent.length - 1];
        joined.push(adjustedCurrent);
    }

    return joined.flat();
};

/**
 * @param {Array<LegMetrics & { typeId: number; typeKey: string }>} allMetrics
 */
const computeMetricsByKey = (allMetrics) => {
    // sumDistance is continuous between legs
    // sumDuration is not continuous, always starts from 0
    /** @type {MetricsByKey} */
    const metricsByKey = {
        sumDistance: allMetrics.map((x) => x.sumDistance).flat(),
        sumDuration: joinSumDuration(allMetrics.map((x) => x.sumDuration)),
        directTimestamp: allMetrics.map((x) => x.directTimestamp).flat(),
        directSpeed: allMetrics.map((x) => x.directSpeed).flat(),
        directPower: allMetrics.map((x) => x.directPower).flat(),
        directElevation: allMetrics.map((x) => x.directElevation).flat(),
        directHeartRate: allMetrics.map((x) => x.directHeartRate).flat(),
        polyline: allMetrics.map((x) => (x.geoPolylineDTO ? x.geoPolylineDTO.polyline : [])).flat()
    };
    // why is last item duplicated? @see calculateLegIndexData
    duplicateLastItemOfEveryMember(metricsByKey);

    return metricsByKey;
};

/**
 * Fills up the store with data fetched from V1 endpoint
 * This way of fetching data is no longer used, just for debugging purposes.
 * E.g. when data coarseness is desired, this can be artificially achieved by setting maxChartSize
 *
 *
 * Difference compared to fetchActivityDataSaga():
 * - metrics for every child are fetched separately
 * - metrics are manually concatenated
 * - ABILITY to set maxChartSize: sets the number of recordings for that child's metrics
 * - ABILITY to set maxPolyLineSize
 *
 * @param {*} action
 */
export function* fetchActivityDataUsingV1EndpointSaga(action) {
    const { activityId } = action.payload;
    try {
        yield put(fetchActivityDataStart());
        /** @type {{ data: ActivityDataDTO }} */
        const response = yield call(gcApi.get, `/activity-service/activity/${activityId}`);
        const { data: activityData } = response;
        yield put(fetchActivityDataSuccess({ activityData }));

        if (activityData.isMultiSportParent) {
            const { childIds } = activityData.metadataDTO;
            /** @type {ActivityDataDTO[]} */
            const childActivities = yield call(fetchChildActivityData, childIds);
            /** @type {LegMetricsByActivityId} */
            const legMetricsByActivityId = yield call(fetchLegMetrics, childIds);

            /** @type {Array<LegMetrics & { typeId: number; typeKey: string }>} */
            const allMetrics = childActivities.map((activityDataDTO) => ({
                ...legMetricsByActivityId[activityDataDTO.activityId],
                typeId: activityDataDTO.activityTypeDTO.typeId,
                typeKey: activityDataDTO.activityTypeDTO.typeKey
            }));

            const metricsByKey = computeMetricsByKey(allMetrics);
            const legIndexData = calculateLegIndexData(allMetrics);

            yield put(fetchActivityDataSuccessV1({ metricsByKey, legIndexData }));
        }
    } catch (error) {
        yield put(fetchActivityDataFail(/** @type {{response: {status: number}}} */ (error)));
    }
}
